import React, { useState } from 'react';
import {
    HiChevronDown,
    HiReceiptPercent,
    HiArrowDownTray,
    HiCalendarDays,
    HiHashtag,
    HiCircleStack,
    HiCreditCard,
} from 'react-icons/hi2';

const InvoicesTab = ({
    invoices,
    loading,
    error,
    onDownloadInvoice,
    formatDate,
    getItemTypeClass,
    getItemTypeLabel,
    expandInvoiceId,
}) => {
    const [expandedInvoices, setExpandedInvoices] = useState(
        expandInvoiceId ? { [expandInvoiceId]: true } : {}
    );

    const toggleInvoice = (invoiceId) => {
        setExpandedInvoices((prev) => ({
            ...prev,
            [invoiceId]: !prev[invoiceId],
        }));
    };

    React.useEffect(() => {
        if (expandInvoiceId) {
            setExpandedInvoices({ [expandInvoiceId]: true });
        }
    }, [expandInvoiceId]);

    if (loading) {
        return (
            <div className="my-accounts-content">
                <p className="my-accounts-loading">Loading invoices...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="my-accounts-content">
                <div className="my-accounts-empty-state">
                    <HiReceiptPercent className="my-accounts-empty-icon" />
                    <h3 className="my-accounts-empty-title">Unable to load</h3>
                    <p className="my-accounts-empty-message">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="my-accounts-content">
            {invoices.length === 0 ? (
                <div className="my-accounts-empty-state">
                    <HiReceiptPercent className="my-accounts-empty-icon" />
                    <h3 className="my-accounts-empty-title">No invoices yet</h3>
                    <p className="my-accounts-empty-message">
                        Your invoices will appear here once you make a purchase.
                    </p>
                </div>
            ) : (
                invoices.map((invoice) => {
                    const isInvoiceExpanded = !!expandedInvoices[invoice.invoice_id];
                    const currencySymbol = invoice.currency_symbol || '';

                    return (
                        <div key={invoice.invoice_id} className="my-accounts-invoice-card">
                            <div
                                className="my-accounts-invoice-header"
                                onClick={() => toggleInvoice(invoice.invoice_id)}
                            >
                                <div className="my-accounts-invoice-header-left">
                                    <div className="my-accounts-order-meta">
                                        <span className="my-accounts-invoice-id">
                                            <HiHashtag className="my-accounts-inline-icon" />
                                            Invoice {invoice.invoice_id}
                                        </span>
                                        <span className="my-accounts-status-badge">
                                            {invoice.order_status}
                                        </span>
                                    </div>
                                    <span className="my-accounts-invoice-date">
                                        <HiCalendarDays className="my-accounts-inline-icon" />
                                        Issued {formatDate(invoice.created_at)}
                                    </span>
                                    {(invoice.created_by_user_name || invoice.created_by_user_phone || invoice.created_by_user_email || invoice.full_billing_address) && (
                                        <div className="my-accounts-invoice-details-panel">
                                            {invoice.created_by_user_name && (
                                                <div className="my-accounts-invoice-detail-row">
                                                    <span className="my-accounts-invoice-detail-label">Name</span>
                                                    <span className="my-accounts-invoice-detail-value">{invoice.created_by_user_name}</span>
                                                </div>
                                            )}
                                            {invoice.created_by_user_phone && (
                                                <div className="my-accounts-invoice-detail-row">
                                                    <span className="my-accounts-invoice-detail-label">Phone</span>
                                                    <span className="my-accounts-invoice-detail-value">{invoice.created_by_user_phone}</span>
                                                </div>
                                            )}
                                            {invoice.created_by_user_email && (
                                                <div className="my-accounts-invoice-detail-row">
                                                    <span className="my-accounts-invoice-detail-label">Email</span>
                                                    <span className="my-accounts-invoice-detail-value">{invoice.created_by_user_email}</span>
                                                </div>
                                            )}
                                            {invoice.full_billing_address && (
                                                <div className="my-accounts-invoice-detail-row my-accounts-invoice-detail-row-address">
                                                    <span className="my-accounts-invoice-detail-label">Billing Address</span>
                                                    <span className="my-accounts-invoice-detail-value">{invoice.full_billing_address}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="my-accounts-invoice-header-right">
                                    <span className="my-accounts-invoice-method">
                                        {invoice.payment_method}
                                    </span>
                                    <span className="my-accounts-invoice-total">
                                        {currencySymbol} {parseFloat(invoice.total_price).toFixed(2)}
                                    </span>
                                    <HiChevronDown
                                        className={`my-accounts-expand-icon ${isInvoiceExpanded ? 'expanded' : ''}`}
                                    />
                                </div>
                            </div>

                            {isInvoiceExpanded && (
                                <div className="my-accounts-order-body">
                                    <h4 className="my-accounts-section-title">
                                        <HiReceiptPercent className="my-accounts-section-icon" />
                                        Invoice Items ({invoice.invoice_items.length})
                                    </h4>

                                    <div className="container-fluid px-0">
                                        <div className="row g-3">
                                            {invoice.invoice_items.map((invItem) => (
                                                <div key={invItem.invoice_item_id} className="col-12">
                                                    <div className="my-accounts-line-card">
                                                        <div className="row g-3 align-items-start">
                                                            <div className="col-12 col-lg-5">
                                                                <div className="my-accounts-line-item-name">{invItem.item_name}</div>
                                                                {invItem.user_name && (
                                                                    <div className="my-accounts-line-user">{invItem.user_name}</div>
                                                                )}
                                                                <span className={`my-accounts-item-type-badge ${getItemTypeClass(invItem.item_type)}`}>
                                                                    {getItemTypeLabel(invItem.item_type)}
                                                                </span>
                                                            </div>

                                                            <div className="col-6 col-md-4 col-lg-3">
                                                                <div className="my-accounts-mini-stat">
                                                                    <span className="my-accounts-mini-stat-label">
                                                                        <HiCircleStack className="my-accounts-inline-icon" />
                                                                        Quantity
                                                                    </span>
                                                                    <span className="my-accounts-mini-stat-value">{invItem.quantity}</span>
                                                                </div>
                                                                <div className="my-accounts-mini-stat">
                                                                    <span className="my-accounts-mini-stat-label">Credits</span>
                                                                    <span className="my-accounts-mini-stat-value">
                                                                        {invItem.item_type === 'credit_package' ? (invItem.credits || '-') : '-'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="col-6 col-md-4 col-lg-2">
                                                                <div className="my-accounts-mini-stat">
                                                                    <span className="my-accounts-mini-stat-label">Unit Price</span>
                                                                    <span className="my-accounts-mini-stat-value">
                                                                        {currencySymbol} {parseFloat(invItem.unit_price || 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                                <div className="my-accounts-mini-stat">
                                                                    <span className="my-accounts-mini-stat-label">Unit Discount</span>
                                                                    <span className="my-accounts-mini-stat-value my-accounts-mini-stat-discount">
                                                                        {currencySymbol} {parseFloat(invItem.unit_discount || 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="col-12 col-md-4 col-lg-2">
                                                                <div className="my-accounts-line-total-card">                                                                    
                                                                    <span className="my-accounts-mini-stat-value">
                                                                        Sub Total Value {currencySymbol} {parseFloat(invItem.unit_price || 0) * (invItem.quantity || 0).toFixed(2)}
                                                                    </span>
                                                                    <span className="my-accounts-line-total-sub">
                                                                        Total discount {currencySymbol} {parseFloat(invItem.total_discount || 0).toFixed(2)}
                                                                    </span>
                                                                    <span className="my-accounts-mini-stat-label">
                                                                        <HiCreditCard className="my-accounts-inline-icon" />
                                                                        Total
                                                                    </span>
                                                                    <span className="my-accounts-line-total-value">
                                                                        {currencySymbol} {parseFloat(invItem.total_price).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="my-accounts-order-summary">
                                        <div className="row g-3">
                                            <div className="col-12 col-md-4">
                                                <div className="my-accounts-summary-block">
                                                    <span className="my-accounts-summary-label">Subtotal</span>
                                                    <span className="my-accounts-summary-value">
                                                        {currencySymbol} {parseFloat(invoice.sub || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="my-accounts-summary-block">
                                                    <span className="my-accounts-summary-label">Discount</span>
                                                    <span className="my-accounts-summary-value my-accounts-summary-discount">
                                                        {currencySymbol} {parseFloat(invoice.discount_amount || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="my-accounts-summary-block my-accounts-summary-block-total">
                                                    <span className="my-accounts-summary-label">Total</span>
                                                    <span className="my-accounts-summary-value">
                                                        {currencySymbol} {parseFloat(invoice.total_price).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                            {parseFloat(invoice.tax_amount || 0) > 0 && (
                                                <div className="col-12 col-md-4">
                                                    <div className="my-accounts-summary-block">
                                                        <span className="my-accounts-summary-label">Tax</span>
                                                        <span className="my-accounts-summary-value">
                                                            {currencySymbol} {parseFloat(invoice.tax_amount).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="my-accounts-action-row">
                                        <button
                                            className="my-accounts-download-btn"
                                            onClick={() => onDownloadInvoice(invoice)}
                                        >
                                            <HiArrowDownTray className="my-accounts-download-icon" />
                                            Download Invoice
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default InvoicesTab;
